import URI from "urijs";
import {
  Envs,
  type LocationWithOnlyPathnameRequired,
  type LocationWithOptionalState,
  ManagedQueryParameters,
  QueryStringLanguage,
  UserLanguage,
} from "../@types";
import { useLocation } from "react-router-dom";
import { planComparePath } from "./constants";
import { mGovUrls } from "./constants/medicareGovUrls";
import { logError } from "./errors";

export const subdomains = ["test", "dev", "imp", "www"] as const;
export type Subdomain = (typeof subdomains)[number];

export const getEnvironment = (): Envs => {
  // If a Vitest test hits this helper and hasn't set `window.location.hostname`,
  // the test will fail. Since Vitest provides a way to detect, and since this helper
  // has to return an `Env`, just return `Envs.test`. If this ever breaks a test,
  // `window.location.hostname` will have to be definitively set and this block
  // removed (or modified)
  if ("vitest" in import.meta) {
    return Envs.test;
  }
  const hostname = window?.location?.hostname;
  if (
    hostname?.includes("localhost") ||
    hostname?.includes("127.0.0.1") ||
    hostname?.includes("0.0.0.0")
  ) {
    return Envs.local;
  }

  if (hostname?.includes("test")) {
    return Envs.test;
  }

  if (hostname?.includes("dev")) {
    return Envs.dev;
  }

  if (hostname?.includes("imp")) {
    return Envs.imp;
  }

  return Envs.prod;
};

export const getLinkWithLanguage = (
  url: string,
  language: UserLanguage
): string => {
  const medicareSubdomain: UserLanguage.Spanish | Extract<Subdomain, "www"> =
    language === UserLanguage.Spanish ? UserLanguage.Spanish : "www";
  return `https://${medicareSubdomain}.${url}`;
};

/**
 * @returns The MCT subdomain for each possible environment. Associates local
 * (e.g., `localhost`) with the TEST environment
 */
export const getSubdomainForEnvironment = (): Subdomain => {
  const environment = getEnvironment();
  const subdomainMap: Record<Envs, Subdomain> = {
    [Envs.local]: "test",
    [Envs.test]: "test",
    [Envs.dev]: "dev",
    [Envs.imp]: "imp",
    [Envs.prod]: "www",
  };
  return subdomainMap[environment];
};

export const getLinkWithLanguageAndEnvironment = (
  url: string,
  language: UserLanguage
): string => {
  const subdomain = getSubdomainForEnvironment();
  const link = new URI(`https://${subdomain}.${url}`);

  link.addQuery("lang", userToQueryStringLangMap[language]);

  return link.toString();
};

export const userToQueryStringLangMap: Record<
  UserLanguage,
  QueryStringLanguage
> = {
  [UserLanguage.English]: QueryStringLanguage.ENGLISH,
  [UserLanguage.Spanish]: QueryStringLanguage.SPANISH,
};

/**
 * Takes a React Router location object and converts it to a URIJS object, retaining
 * the `pathname`, `search`, and `hash` values. Does not preserve `location.state`.
 *
 * Only `pathname` is required in the location object, given that we may want to
 * manually construct objects and often that's the only value that we might care about
 */
export const convertLocationToUri = (
  location: LocationWithOnlyPathnameRequired
): ReturnType<typeof URI> =>
  new URI(location.pathname)
    .search(location?.search || "")
    .hash(location?.hash || "");

/**
 * Takes a URIJS object and converts it to a React Router location object, retaining
 * the `pathname`, `search`, and `hash` values
 */
export const convertUriToLocation = (
  uri: URI
): Pick<RouterLocation, "hash" | "pathname" | "search"> => ({
  pathname: uri.pathname(),
  search: uri.search(),
  hash: uri.hash(),
});

/**
 * Takes a React Router location object and converts it to a URIJS object, retaining
 * the `pathname`, `search`, and `hash` values, and also stripping managed query
 * parameters (`lang` and `year`) from the `search` value
 *
 * Only `pathname` is required in the location object, given that we may want to
 * manually construct objects and often that's the only value that we might care about
 */
export const convertLocationToUriStripManagedParams = (
  location: LocationWithOnlyPathnameRequired
): ReturnType<typeof URI> => {
  const uri = convertLocationToUri(location);
  uri.removeSearch(Object.values(ManagedQueryParameters));
  return uri;
};

/**
 * Takes current a location object, and returns a URL string that has managed query
 * params (`lang` and `year`) stripped out.
 *
 * Only `pathname` is required in the location object, given that we may want to
 * manually construct objects and often that's the only value that we might care about
 *
 * The returned string will contain `pathname`, `hash`, `search`, depending on what
 * was passed in
 */
export const convertLocationToStringStripManagedParams = (
  location: LocationWithOnlyPathnameRequired
): string => convertLocationToUriStripManagedParams(location).valueOf();

/**
 * Takes current a location object, and returns a URL string
 *
 * Use this for any case in which managed query params will not change between
 * the time when the link is rendered and when it is used: E.g., internal links
 * within a single page (route). Do not use for cases where a location object or
 * its string value is passed as `location.state`, as a user may change, e.g., language
 * or search year before using it in a return link
 */
export const convertLocationToString = (
  location: LocationWithOnlyPathnameRequired
): string => convertLocationToUri(location).valueOf();

/**
 * Takes current a location object, and returns a modified object that has managed
 * query params (`lang` and `year`) stripped out.
 *
 * Only `pathname` is required in the location object, given that we may want to
 * manually construct objects and often that's the only value that we might care about
 *
 * The returned object or will contain `pathname`, `hash`, `search`, and
 * optionally `state` and/or `key`, depending on what was passed in
 */
export const stripManagedQueryParamsFromLocation = (
  location: LocationWithOnlyPathnameRequired
): LocationWithOptionalState => {
  const uri = convertLocationToUriStripManagedParams(location);
  return {
    ...location,
    search: uri.search(),
    hash: uri.hash(), // normalizes hash, prefixed with `#`
  };
};

/**
 * If a `returnTo` value is set in query (search) params, this hook will return
 * it. Useful to control or override where a "Back" button will take someone (as
 * opposed to the browser's back button)
 *
 * The `returnTo` URL string (`pathname` plus `search` and `hash`, if there are any)
 * will have managed query params (`lang` and `year`) stripped out from the `search`
 * portion of the string. This is to prevent locking down values that can be changed
 * by a user on any page (or a limited set of pages, in the case of `year`)
 *
 * Note: This would be cleaner if it used `history.push.state`, but that option
 * does not exist when using `HashRouter`
 */
export const useReturnTo = () => {
  const location = useLocation();
  const uri = convertLocationToUriStripManagedParams(location);
  const { returnTo } = uri.search(true);
  const hash = uri.hash();

  return returnTo ? `${returnTo}${hash}` : undefined;
};

/**
 * Takes current and link target location objects, and creates modified target location
 * in which the `search` string contains a `returnTo` URL that will bring the user
 * back to the place where they used the link
 *
 * Only `pathname` is required in either location object, given that we may want to
 * manually construct objects and often that's the only value that we might care about
 */
export const createTargetLocationWithReturnTo = ({
  currentLocation,
  targetLocation,
}: {
  currentLocation: LocationWithOnlyPathnameRequired;
  targetLocation: LocationWithOnlyPathnameRequired;
}): LocationWithOptionalState => {
  const returnToUri = convertLocationToUriStripManagedParams(currentLocation);
  const targetUriSearch = URI(targetLocation.search || "");
  targetUriSearch.addSearch("returnTo", returnToUri);
  const search = targetUriSearch.search();
  return {
    ...targetLocation,
    hash: targetLocation.hash || "",
    search,
  };
};

export const getIsInternalAnchorLinkHref = (href: string) => {
  const env = getEnvironment();
  const isLocal = env === Envs.local;
  // link is internal to MCT and contains two hashes - Aka, a named anchor
  const internalAnchorLinkRe = new RegExp(
    `.*${isLocal ? location.hostname : planComparePath}/#.*#`
  );
  return internalAnchorLinkRe.test(href);
};

/**
 * Gets the value to explicitly set as the `referrer` header in MCTAPI
 * requests. This value must end in a trailing slash for an API key to be generated.
 * Along with setting `referrerPolicy` to `strict-origin-when-cross-origin`, we
 * hope to minimize or prevent the `referer` (sic) header from being truncated (i.e.,
 * the pathname being removed), which causes 401 errors
 */
export const getReferrer = () => {
  let referrer = `${window.location.origin}${window.location.pathname}`;
  if (referrer.charAt(referrer.length - 1) !== "/") {
    referrer = `${referrer}/`;
  }
  return referrer;
};

export const getPageNameFromPathName = (pathname: string) =>
  pathname.replace("/", "").split("/", 1).join("").replaceAll("-", " ");

/**
 * Takes in a URL and an MCT subdomain to insert at the beginning of the hostname
 * for the returned URL.
 *
 * If the URL has a non-standard subdomain -- e.g., a language or a language plus
 * a Medicare-specific environment, this helper will strip those and replace them
 * with the subdomain passed in.
 *
 * If the passed in URL is invalid, it will return the default MCT anonymous landing
 * page for the provided subdomain
 *
 * NOTE: This helpers associates local (e.g., `localhost`) with the TEST environment.
 * If you want local URLs to stay pointing at your local, simply skip running this
 * helper in that environment. (local doesn't have a subdomain unless aliased by your server)
 *
 * @returns a URL with the passed in subdomain
 */
export const setSubdomain = ({
  subdomain,
  url,
}: {
  subdomain: Subdomain;
  url: string;
}) => {
  let parsedUrl = new URL(
    `https://${subdomain}.${mGovUrls.medicareHome}/${planComparePath}`
  );
  try {
    parsedUrl = new URL(url);
  } catch (e) {
    logError("Invalid URL passed to setSubdomain helper", e as Error);
    return parsedUrl.toString();
  }
  let hostnameArray = parsedUrl.hostname.split(".");
  // Possible that there is no subdomain set (hostnameArray is only 2 values)
  if (hostnameArray.length > 2) {
    hostnameArray = hostnameArray.slice(1);
  }
  const possibleSubdomains: Subdomain[] = ["test", "dev", "imp", "www"];
  // A URL may start with a language subdomain and a secondary environment subdomain,
  // in which case all that needs to be done is for the language portion to be stripped
  const newhostnameArray =
    hostnameArray[0] === subdomain
      ? hostnameArray
      : // if the subdomain passed in is different, swap it with the new one
        hostnameArray.length === 3 &&
          possibleSubdomains.includes(hostnameArray[0] as Subdomain)
        ? [subdomain, ...hostnameArray.slice(1)]
        : // Otherwise, add the environment subdomain to the rest of the hostname
          [subdomain, ...hostnameArray];
  parsedUrl.hostname = newhostnameArray.join(".");
  return parsedUrl.toString();
};

type RouterLocation = ReturnType<typeof useLocation>;

/**
 * Get the fragment appended to the end of a URL, after a terminal hash
 * (as opposed to the `HashRouter` hash, used for routing). Does not include the
 * leading `#` symbol
 *
 * Can optionally fall back to the value provided by the router's `location.hash` field
 *
 * In the RegEx, (?<name>pattern) captures "pattern" and assigns it to the group named "name"
 */
export const getUrlFragment = (hash?: RouterLocation["hash"]) => {
  const locationFragment = hash?.slice()?.replace(/#/g, "");
  return (
    window.location.hash.match(/[^/]#(?<hash>.*)$/)?.groups?.hash ??
    locationFragment ??
    ""
  );
};

/**
 * Returns the `pathname` portion of the hash-routed `window.location.hash`
 * Can optionally fall back to the value provided by the router's `location.pathname` field
 */
export const getRouterPathnameFromUrlHash = (
  pathname?: RouterLocation["pathname"]
) => {
  const hashedRoutePathName = window.location.hash.match(
    /#\/(?<pathname>[^?|^#]*)/
  )?.groups?.pathname;
  return hashedRoutePathName ? `/${hashedRoutePathName}` : (pathname ?? "");
};

/**
 * Returns the `search` or `query` (params) portion of the hash-routed `window.location.hash`
 * Can optionally fall back to the value provided by the router's `location.search` field
 */
export const getRouterSearchFromUrlHash = (
  search?: RouterLocation["search"]
) => {
  const hashedRouteSearch =
    window.location.hash.match(/\?(?<search>[^#]*)/)?.groups?.search;
  return hashedRouteSearch ? `?${hashedRouteSearch}` : (search ?? "");
};

/**
 * Returns the primary parts of a React Router Location descriptor (hash, pathname, search)
 * without using the router's objects (so, e.g., no `location` dependency needed in an effect).
 *
 * Can optionally fall back to values provided by the router's `location` object
 */
export const getRouterLocationFromWindowLocation = (
  location?: Partial<RouterLocation>
): Pick<RouterLocation, "hash" | "pathname" | "search"> => {
  const fragment = getUrlFragment(location?.hash);
  const pathname = getRouterPathnameFromUrlHash(location?.pathname);
  const search = getRouterSearchFromUrlHash(location?.search);
  return {
    hash: `#${fragment}`,
    pathname,
    search,
  };
};
